'use client';

/**
 * ResumeModal — P0-75 Phase 5 cross-device lock resolver.
 *
 * Shown when `useCurrentSession().lockStatus === 'elsewhere'`: another device
 * is actively holding the user's session. The user must explicitly decide
 * whether to force-claim here (evicting the other device) or start a fresh
 * chat locally. Hard lock is intentional — per BRD §13.5 we prompt rather
 * than silently strand one of the tabs.
 */

import { useState } from 'react';
import { useCurrentSession } from '@flowstack/sdk';

interface ResumeModalProps {
  /** Called when the user chooses "Start new chat". Parent typically navigates
   *  to /chat with a fresh session_id. */
  onStartNew: () => void;
  /** Called after a successful force-claim. Parent typically navigates to
   *  /chat?session_id={sessionId}. */
  onResumeHere: (sessionId: string) => void;
}

export default function ResumeModal({ onStartNew, onResumeHere }: ResumeModalProps) {
  const { currentSession, lockStatus, claim, release } = useCurrentSession();
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  if (lockStatus !== 'elsewhere' || !currentSession?.sessionId) return null;

  const handleForceClaim = async () => {
    if (!currentSession.sessionId) return;
    setBusy(true);
    setLocalError(null);
    const ok = await claim(currentSession.sessionId, { force: true });
    setBusy(false);
    if (ok) onResumeHere(currentSession.sessionId);
    else setLocalError('Unable to claim session. Try again.');
  };

  const handleStartNew = async () => {
    setBusy(true);
    // Best-effort release of any stale token on this device, then hand off.
    await release().catch(() => undefined);
    setBusy(false);
    onStartNew();
  };

  const otherDevice = currentSession.deviceDescription || 'another device';
  const lastActivity = formatAgo(currentSession.lastActivityAt);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] shadow-2xl p-5">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">
          Your session is active on {otherDevice}
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mt-2">
          Another device is currently holding your chat session
          {lastActivity ? ` (last activity ${lastActivity})` : ''}. You can
          resume here — this will disconnect the other device — or start a
          fresh chat on this one.
        </p>

        {localError && (
          <div className="mt-3 text-xs text-[var(--color-error,#d44343)]">{localError}</div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={handleStartNew}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            Start new chat
          </button>
          <button
            onClick={handleForceClaim}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded-lg bg-[var(--color-accent)] text-white font-medium hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Claiming…' : 'Resume here'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatAgo(epochSeconds: number | null): string {
  if (!epochSeconds) return '';
  const now = Math.floor(Date.now() / 1000);
  const diff = now - epochSeconds;
  if (diff < 30) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
}
